/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {SensorType} from '@ion/core/src/types/entities';
import {EmptyStateRow} from '@ion/dls/src/components/assetdetails/current/EmptyStateRow';
import NoDataBadge from '@ion/dls/src/components/badges/NoDataBadge';
import {LightIcon} from '@ion/dls/src/icons/current/LightIcon';
import {ShockIcon} from '@ion/dls/src/icons/current/ShockIcon';
import {TemperatureIcon} from '@ion/dls/src/icons/current/TemperatureIcon';
import {TiltIcon} from '@ion/dls/src/icons/current/TiltIcon';
import {SensorTypeIcon} from '@ion/dls/src/icons/SensorTypeIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';

export interface NoSensorDataProps {
  sensorType?: string;
  iconSize?: number | IconSize,
  displayIcon?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    container : {
      width          : '100%',
      display        : 'flex',
      alignContent   : 'center',
      justifyContent : 'space-between'
    },
    iconContainer : {
      display                        : 'flex',
      alignItems                     : 'center',
      justifyContent                 : 'center',
      [theme.breakpoints.down('sm')] : {
        marginLeft : '0px',
        minWidth   : '100px'
      }
    },
    text : {
      fontFamily : 'Roboto',
      fontSize   : '14px',
      maxWidth   : '150px',
      color      : dlsColor('grey3')

    }
  };
});

// the current theme's 24px icon per SC sensor type ...
const CURRENT_SENSOR_ICONS = {
  [SensorType.Temperature] : TemperatureIcon,
  [SensorType.Light]       : LightIcon,
  [SensorType.Shock]       : ShockIcon,
  [SensorType.Tilt]        : TiltIcon,
  [SensorType.Tilted]      : TiltIcon
};

// the current theme's sensor icon - its own design icon for an SC sensor,
// the shared sensor type icon for any other ...
const CurrentSensorIcon = ({sensorType}: {sensorType?: string}) => {
  const Icon = CURRENT_SENSOR_ICONS[sensorType];
  return Icon
    ? <Icon size={IconSize.medium} color={dlsColor('grey1')} />
    : <SensorTypeIcon sensorType={sensorType}
      color={dlsColor('grey1')}
      size={IconSize.medium} />;
};

/**
 * NoSensorData is a component used to display when there has been no SC Device
 * sensor data within the past 24 hours.
 *
 * ```typescript
 * import NoSensorData from './NoSensorData';
 *
 * if (!isDataAvailable) {
 *   return (
 *     <div className={classes.noData}>
 *       <NoSensorData sensorType={sensorType} iconSize={IconSize.medium} />
 *     </div>
 *   );
 * }
 * ...
 * ```
 */
const NoSensorData = ({sensorType, iconSize, displayIcon = true}: NoSensorDataProps) => {
  const classes   = useStyles();
  const isCurrent = isCurrentTheme();

  return isCurrent ? (
    <EmptyStateRow message={translate('tracking:assetDetail.noSensorDataPastDay')}
      tagLabel={translate('common:noData.noData')}
      icon={displayIcon && <CurrentSensorIcon sensorType={sensorType} />} />
  ) : (
    <div className={classes.container}>
      <span className={classes.text}>
        {translate('common:noData.day')}
      </span>
      <div className={classes.iconContainer}>
        {displayIcon && (
          <span style={{marginRight: '4px'}}>
            <SensorTypeIcon sensorType={sensorType} color={dlsColor('grey4')} size={iconSize} />
          </span>
        )}
        <NoDataBadge />
      </div>
    </div>
  );
};

export default NoSensorData;
